import { JSX, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Divider } from "antd";
import { useQuery, useQueryClient } from "react-query";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import { CardContainer } from "../../components/Card/CardContainer";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import StepAntd from "../../components/StepAntd/StepAntd";
import { resolveShopTierConfig } from "../ShopTier/config";
import { ConfigTierBenefit } from "../ShopTier/mockData";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { useMessageContext } from "../../context/MessageProvider";
import { getTierProgramDetail, updateTierProgram, updateTierProgramFiles } from "../../datasource/TierProgramDatasource";
import Step2, { Step2Result } from "./CreateShopTierBenefitStep/Step2";
import Step3 from "./CreateShopTierBenefitStep/Step3";
import { saveHowItWorksAndTierLevels } from "./saveTierProgramContent";
import { buildTierAuditDiff } from "./buildTierAuditDiff";

// While true, Save only logs the audit payload and sends nothing to the API.
const AUDIT_DRY_RUN = true;

function EditShopTierBenefit(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { messageApi } = useMessageContext();
  // The route param is named `year`, but ShopTierBenefit navigates here with the tierProgramId.
  const { year: tierProgramId } = useParams<{ year: string }>();
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);

  // Same key and fetcher as Step2, so both read one cached request.
  const { data: tierProgramDetail } = useQuery(
    ["tier-program-detail", tierProgramId],
    () => getTierProgramDetail(tierProgramId as string),
    { enabled: !!tierProgramId, refetchOnWindowFocus: false },
  );
  const programYear: number = tierProgramDetail?.responseData?.year ?? 0;

  const [step, setStep] = useState<number>(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(() => new Set([0]));
  const [editResult, setEditResult] = useState<Step2Result | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setVisitedSteps((prev) => (prev.has(step) ? prev : new Set(prev).add(step)));
  }, [step]);

  const stepItems = [
    { title: "แก้ไขสิทธิประโยชน์" },
    { title: "ตรวจสอบ" },
  ];

  const goNext = () => setStep((s) => Math.min(s + 1, stepItems.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const sourceTiers: ConfigTierBenefit[] = [];

  // State updates land on the next render, so a fast double-click could pass the isSaving check twice.
  const savingRef = useRef(false);

  const onSubmit = async (data: Step2Result, reason: string) => {
    if (!tierProgramId || savingRef.current) return;

    const audit = buildTierAuditDiff(tierProgramDetail?.responseData, data, company?.company);
    // if (AUDIT_DRY_RUN) {
    //   console.log("[tier audit] value_before", audit.before);
    //   console.log("[tier audit] value_after", audit.after);
    //   console.log("[tier audit] reason", reason);
    //   console.log("[tier audit] value_before (string)", (audit.before));
    //   console.log("[tier audit] value_after (string)", (audit.after));
    //   messageApi.info("โหมดทดสอบ: ยังไม่ได้ส่งข้อมูลไป API ดูค่าได้ใน console");
    //   return;
    // }

    // Set right before the try so the finally below always releases it.
    savingRef.current = true;
    setIsSaving(true);
    try {
      await updateTierProgram(
        tierProgramId,
        JSON.stringify(data?.faqs!.map((faq) => ({
          title: faq.question,
          detail: faq.answer,
        }))),
        JSON.stringify(data?.rules!.map((rule) => ({
          title: rule.title,
          detail: rule.content,
        }))),
        JSON.stringify(audit.before),
        JSON.stringify(audit.after),
        reason,
        company?.firstname + " " + company?.lastname,
        data.bannerFile
      )
      await saveHowItWorksAndTierLevels(tierProgramId, data, company);
      queryClient.invalidateQueries(["tier-program-detail", tierProgramId]);
      queryClient.invalidateQueries(["tier-program-tier-level", tierProgramId]);
      // messageApi.success("บันทึกการแก้ไขสำเร็จ");
      navigate(-1);
    } catch (err) {
      console.log(err);
      messageApi.error("บันทึกการแก้ไขไม่สำเร็จ");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const titleYear = programYear ? ` ปี ${programYear}` : "";

  const stepsComponents = [
    <Step2
      key={0}
      mode="edit"
      config={config}
      sourceTiers={sourceTiers}
      tierProgramId={tierProgramId}
      company={company.company}
      onBack={() => navigate(-1)}
      onNext={(result) => {
        setEditResult(result);
        goNext();
      }}
    />,
    <Step3
      key={1}
      mode="edit"
      config={config}
      year={programYear}
      data={editResult}
      sourceTiers={sourceTiers}
      onBack={goBack}
      onConfirm={(reason) => onSubmit(editResult!, reason)}
      loading={isSaving}
    />,
  ];

  return (
    <CardContainer>
      <PageTitleNested
        title={`แก้ไขสิทธิประโยชน์${titleYear}`}
        showBack
        onBack={() => (step === 0 ? navigate(-1) : goBack())}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ตั้งค่าระดับ", path: -1 },
              { text: `แก้ไขสิทธิประโยชน์${titleYear}`, path: window.location.pathname },
            ]}
          />
        }
        extra={<StepAntd current={step} items={stepItems} />}
      />
      <Divider />
      {stepsComponents.map((comp, idx) =>
        visitedSteps.has(idx) ? (
          <div key={idx} style={{ display: step === idx ? "block" : "none" }}>
            {comp}
          </div>
        ) : null,
      )}
    </CardContainer>
  );
}

export default EditShopTierBenefit;
